import React, { useCallback, useState } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import { Wand2, AlertCircle } from 'lucide-react';
import { useMonacoTheme } from '../../hooks';

type ContentType = 'json' | 'xml' | 'text' | 'html' | 'javascript';

interface BodyEditorProps {
  content: string;
  contentType: ContentType;
  onChange: (content: string) => void;
  onContentTypeChange: (contentType: ContentType) => void;
}

const LANGUAGE_MAP: Record<string, string> = {
  json: 'json',
  xml: 'xml',
  text: 'plaintext',
  html: 'html',
  javascript: 'javascript',
};

export function BodyEditor({
  content,
  contentType,
  onChange,
  onContentTypeChange,
}: BodyEditorProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const monacoTheme = useMonacoTheme();

  // Handle beautify/format
  const handleBeautify = useCallback(() => {
    if (contentType === 'json' && content.trim()) {
      try {
        const parsed = JSON.parse(content);
        const formatted = JSON.stringify(parsed, null, 2);
        onChange(formatted);
        setValidationError(null);
      } catch (e) {
        setValidationError(e instanceof Error ? e.message : 'Invalid JSON');
      }
    }
  }, [content, contentType, onChange]);

  // Validate JSON on change
  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      const newContent = value || '';
      onChange(newContent);

      if (contentType === 'json' && newContent.trim()) {
        try {
          JSON.parse(newContent);
          setValidationError(null);
        } catch (e) {
          setValidationError(e instanceof Error ? e.message : 'Invalid JSON');
        }
      } else {
        setValidationError(null);
      }
    },
    [contentType, onChange]
  );

  // Configure Monaco editor
  const handleEditorMount = useCallback(
    (editor: any, monaco: Monaco) => {
      // Set editor options
      editor.updateOptions({
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 13,
        lineNumbers: 'on',
        renderLineHighlight: 'line',
        tabSize: 2,
        wordWrap: 'on',
        automaticLayout: true,
      });

      // Add format shortcut (Cmd/Ctrl+Shift+F)
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
        () => {
          handleBeautify();
        }
      );
    },
    [handleBeautify]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <select
          value={contentType}
          onChange={(e) =>
            onContentTypeChange(e.target.value as ContentType)
          }
          className="px-3 py-1.5 text-sm bg-background border border-input rounded-md"
        >
          <option value="json">JSON</option>
          <option value="xml">XML</option>
          <option value="text">Text</option>
          <option value="html">HTML</option>
          <option value="javascript">JavaScript</option>
        </select>

        {contentType === 'json' && (
          <button
            onClick={handleBeautify}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-muted hover:bg-accent rounded-md transition-colors"
            title="Beautify JSON (Cmd/Ctrl+Shift+F)"
          >
            <Wand2 className="w-4 h-4" />
            Beautify
          </button>
        )}
      </div>

      <div className="border border-input rounded-md overflow-hidden">
        <Editor
          height="200px"
          language={LANGUAGE_MAP[contentType]}
          value={content}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          theme={monacoTheme}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            lineNumbers: 'on',
            renderLineHighlight: 'line',
            tabSize: 2,
            wordWrap: 'on',
          }}
        />
      </div>

      {validationError && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{validationError}</span>
        </div>
      )}
    </div>
  );
}

export default BodyEditor;
